import { Alert, Button, FileInput, Group, Modal, Stack } from '@mantine/core';
import { IconFileText } from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';
import { parseEraPdf } from '../utils/eraPdfParser';

function isPdfFile(file: File | null): boolean {
  if (!file) return false;
  if (file.type === 'application/pdf') return true;
  return file.name.toLowerCase().endsWith('.pdf');
}

export interface AddEraModalProps {
  opened: boolean;
  onClose: () => void;
}

export function AddEraModal({ opened, onClose }: AddEraModalProps): JSX.Element {
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string>();
  const [submitting, setSubmitting] = useState(false);
  const parseGenerationRef = useRef(0);
  const hasPdf = isPdfFile(file);

  useEffect(() => {
    if (!opened) {
      parseGenerationRef.current += 1;
      setFile(null);
      setError(undefined);
      setSubmitting(false);
    }
  }, [opened]);

  const handleSubmit = async (): Promise<void> => {
    if (!file || !hasPdf) return;

    const resultTab = window.open('about:blank', '_blank');
    if (!resultTab) {
      setError('Allow pop-ups for this site to view the parsed JSON.');
      return;
    }

    const parseGeneration = parseGenerationRef.current + 1;
    parseGenerationRef.current = parseGeneration;
    setSubmitting(true);
    setError(undefined);
    try {
      const report = await parseEraPdf(file);
      if (parseGeneration !== parseGenerationRef.current) {
        resultTab.close();
        return;
      }
      const url = URL.createObjectURL(
        new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' }),
      );
      resultTab.location.replace(url);
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
      onClose();
    } catch (parseError) {
      resultTab.close();
      if (parseGeneration !== parseGenerationRef.current) return;
      setError(parseError instanceof Error ? parseError.message : 'Unable to parse the ERA PDF.');
    } finally {
      if (parseGeneration === parseGenerationRef.current) {
        setSubmitting(false);
      }
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Add ERA"
      centered
      closeOnEscape={!submitting}
      closeOnClickOutside={!submitting}
      withCloseButton={!submitting}
    >
      <Stack gap="md">
        <FileInput
          label="ERA document"
          placeholder="Upload a PDF"
          accept="application/pdf,.pdf"
          leftSection={<IconFileText size={16} />}
          value={file}
          onChange={(next) => setFile(isPdfFile(next) ? next : null)}
          clearable
          withAsterisk
          disabled={submitting}
        />
        {error && <Alert color="red">{error}</Alert>}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            color="gold"
            disabled={!hasPdf}
            loading={submitting}
            onClick={() => void handleSubmit()}
          >
            Submit
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
